import React from 'react'
import PropTypes from 'prop-types'
import Portal from '@aytm/lib/rfd-ui/components/basic/portal'
import styles from '@aytm/lib/rfd-ui/styles/SuggestionsComponent.module.scss'
import getSelectionCoords from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/suggestionsPlugin/lib/get-selection-coords'
import SuggestionItem from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/suggestionsPlugin/components/SuggestionItem'

const KEY_UP = 'ArrowUp'
const KEY_DOWN = 'ArrowDown'
const KEY_ENTER = 'Enter'
const KEY_PAGE_UP = 'PageUp'
const KEY_PAGE_DOWN = 'PageDown'

const DROPDOWN_OFFSET_X_DEFAULT = 10
const DROPDOWN_OFFSET_Y = 24

export default class SuggestionsComponent extends React.PureComponent {
  static propTypes = {
    items: PropTypes.array,
    selectSuggestion: PropTypes.func,
    SuggestionsItemComponent: PropTypes.node,
    visibility: PropTypes.bool
  }

  constructor(props) {
    super(props)
    this.updateItems = this.updateItems.bind(this)
    this.updatePosition = this.updatePosition.bind(this)
    this.calculateDropdownOffsetTop = this.calculateDropdownOffsetTop.bind(this)
    this.extractDropdownOffsetText = this.extractDropdownOffsetText.bind(this)
    this.calculateDropdownTextOffsetLeft = this.calculateDropdownTextOffsetLeft.bind(this)
    this.calculateDropdownOffsetLeft = this.calculateDropdownOffsetLeft.bind(this)
    this.calculatePortalStyles = this.calculatePortalStyles.bind(this)
    this.calculateItemIndexByHeightLimit = this.calculateItemIndexByHeightLimit.bind(this)
    this.calculatePageDownItemIndex = this.calculatePageDownItemIndex.bind(this)
    this.calculatePageUpItemIndex = this.calculatePageUpItemIndex.bind(this)
    this.createSuggestionMouseHandler = this.createSuggestionMouseHandler.bind(this)
    this.scrollToItem = this.scrollToItem.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.renderDropdown = this.renderDropdown.bind(this)
    this.selectSuggestion = this.selectSuggestion.bind(this)
    const { items = [], visibility } = props
    this.refItems = React.createRef()
    this.timeoutId = null
    this.state = {
      selectedIndex: 0,
      portalStyles: this.calculatePortalStyles(items, props),
      items,
      visibility,
      pageX: 0,
      pageY: 0
    }
  }

  // @NOTICE: method for external usage
  updateItems(items, visibility) {
    const preselectedIndex = items.map(({ selected }) => selected).indexOf(true)
    const index = -1 === preselectedIndex ? 0 : preselectedIndex

    this.setState({ selectedIndex: index, items, visibility }, () => {
      this.timeoutId = setTimeout(() => {
        this.scrollToItem({ index, force: true })
        this.updatePosition(items)
        this.timeoutId = null

        clearTimeout(this.timeoutId)
      })
    })
  }

  // @NOTICE: method for external usage
  updatePosition() {
    setTimeout(() => {
      this.setState({
        portalStyles: this.calculatePortalStyles(this.state.items, this.props)
      })
    })
  }

  calculateDropdownOffsetTop() {
    return DROPDOWN_OFFSET_Y
  }

  extractDropdownOffsetText(items) {
    return items && items[0] && items[0].offsetText
  }

  calculateDropdownTextOffsetLeft(items, cursorTextStyle) {
    const offsetText = this.extractDropdownOffsetText(items)

    if (offsetText && cursorTextStyle) {
      const element = document.createElement('div')

      element.textContent = offsetText.replace(' ', '\u00A0')
      element.style.font = cursorTextStyle
      element.style.position = 'absolute'
      element.style.visibility = 'hidden'
      element.style.opacity = '0'
      element.style.zIndex = -1

      document.body.appendChild(element)

      const rects = element.getClientRects()

      document.body.removeChild(element)

      if (rects && rects[0] && rects[0].width) {
        return Math.ceil(rects[0].width)
      }
    }
    return 0
  }

  calculateDropdownOffsetLeft({ items, itemsElementRects, selectionCoords, props }) {
    const itemsWidth = itemsElementRects[0].width
    const textOffsetLeft = this.calculateDropdownTextOffsetLeft(items, props.cursorTextStyle)
    const offset = selectionCoords.x + itemsWidth - DROPDOWN_OFFSET_X_DEFAULT

    return offset < 0 ? offset - textOffsetLeft : -textOffsetLeft
  }

  calculatePortalStyles(items = [], props) {
    if (this.refItems.current) {
      const itemsElement = this.refItems.current
      const itemsElementRects = itemsElement.getClientRects()

      if (itemsElementRects && itemsElementRects[0]) {
        const selectionCoords = getSelectionCoords()
        const elementOffsetTop = window.scrollY || document.documentElement.scrollTop
        const elementOffsetLeft = window.scrollX || document.documentElement.scrollLeft
        const dropdownOffsetLeft = this.calculateDropdownOffsetLeft({
          items,
          itemsElementRects,
          selectionCoords,
          props
        })
        const dropdownOffsetTop = this.calculateDropdownOffsetTop()

        return {
          top: Math.floor(selectionCoords.y + dropdownOffsetTop + elementOffsetTop),
          left: Math.floor(selectionCoords.x + elementOffsetLeft + dropdownOffsetLeft)
        }
      }
    }

    return {
      visibility: 'hidden',
      x: 0,
      y: 0
    }
  }

  calculateItemIndexByHeightLimit(heightLimit) {
    const items = this.state.items

    if (!items || !items.length) {
      return 0
    }

    let index = 0
    let height = 0

    while (index < items.length && height < heightLimit) {
      const element = document.querySelector(`#suggestion-item-${index}`)

      height += element.clientHeight
      index++
    }

    return index
  }

  calculatePageDownItemIndex() {
    const itemsElement = this.refItems.current
    const heightLimit = itemsElement.scrollTop + itemsElement.clientHeight

    if (!itemsElement) {
      return 0
    }

    return this.calculateItemIndexByHeightLimit(heightLimit)
  }

  calculatePageUpItemIndex() {
    const itemsElement = this.refItems.current
    const heightLimit = itemsElement.scrollTop - itemsElement.clientHeight

    if (!itemsElement) {
      return 0
    }

    return this.calculateItemIndexByHeightLimit(Math.max(0, heightLimit))
  }

  createSuggestionMouseHandler(selectedIndex) {
    return event => {
      const { pageX, pageY } = event
      const { selectedIndex: prevSelectedIndex, pageX: prevPageX, pageY: prevPageY } = this.state
      if (prevPageX !== pageX && prevPageY !== pageY) {
        if (prevSelectedIndex !== selectedIndex) {
          this.setState({ pageX, pageY, selectedIndex })
        } else {
          this.setState({ pageX, pageY })
        }
      }
    }
  }

  componentDidUpdate(_, prevState) {
    if (prevState.visibility !== this.state.visibility) {
      if (this.state.visibility) {
        window.addEventListener('keydown', this.handleKeyDown)
      } else {
        window.removeEventListener('keydown', this.handleKeyDown)
      }
    }
  }

  componentDidMount() {
    if (this.state.visibility) {
      window.addEventListener('keydown', this.handleKeyDown)
    }
  }

  componentWillUnmount() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }
    window.removeEventListener('keydown', this.handleKeyDown, true)
  }

  scrollToItem({ index, force = false } = {}) {
    const { items, selectedIndex } = this.state
    const itemIndex = Math.max(0, Math.min(items.length - 1, index))
    const itemElement = document.querySelector(`#suggestion-item-${itemIndex}`)
    const itemsElement = this.refItems.current

    if (selectedIndex === itemIndex || !itemElement || !itemsElement) {
      return
    }

    this.setState({ selectedIndex: itemIndex }, () => {
      const itemOffsetTop = itemElement.offsetTop
      if (force) {
        this.refItems.current.scrollTop = itemOffsetTop
      } else {
        if (itemsElement.scrollTop >= itemOffsetTop) {
          this.refItems.current.scrollTop = itemOffsetTop
        }
        if (itemsElement.scrollTop + itemsElement.clientHeight <= itemOffsetTop + itemElement.clientHeight) {
          this.refItems.current.scrollTop = itemOffsetTop - itemsElement.clientHeight + itemElement.clientHeight
        }
      }
    })
  }

  handleKeyDown(event) {
    const { selectedIndex, items } = this.state
    if (event.key === KEY_UP) {
      this.scrollToItem({ index: selectedIndex - 1 })
      if (selectedIndex > 0) {
        event.preventDefault()
      }
    }
    if (event.key === KEY_DOWN) {
      this.scrollToItem({ index: selectedIndex + 1 })
      if (selectedIndex < items.length - 1) {
        event.preventDefault()
      }
    }
    if (event.key === KEY_PAGE_UP) {
      this.scrollToItem({ index: this.calculatePageUpItemIndex(), force: true })
      event.preventDefault()
    }
    if (event.key === KEY_PAGE_DOWN) {
      this.scrollToItem({ index: this.calculatePageDownItemIndex(), force: true })
      event.preventDefault()
    }
    if (event.key === KEY_ENTER && items[selectedIndex]) {
      this.selectSuggestion(items[selectedIndex])
      event.preventDefault()
    }
  }

  renderDropdown() {
    return (
      <div className={styles.items} ref={this.refItems}>
        {this.state.items.map((suggestion, index) => (
          <div
            key={index}
            onMouseMove={this.createSuggestionMouseHandler(index)}
            onMouseEnter={this.createSuggestionMouseHandler(index)}
          >
            <SuggestionItem
              index={index}
              selectSuggestion={this.selectSuggestion}
              selectedIndex={this.state.selectedIndex}
              suggestion={suggestion}
            />
          </div>
        ))}
      </div>
    )
  }

  selectSuggestion(suggestion) {
    const { selectSuggestion } = this.props

    selectSuggestion(suggestion)
  }

  render() {
    const { visibility, portalStyles } = this.state

    if (!visibility) {
      return null
    }

    return (
      <Portal portalId={'suggestions-items'}>
        <div className={styles.portal} style={portalStyles}>
          <div className={styles.dropdown}>{this.renderDropdown()}</div>
        </div>
      </Portal>
    )
  }
}
